/** Edad a partir de fecha de nacimiento (YYYY-MM-DD). */
export function edadDesdeNacimiento(fecha?: string | null): string {
  if (!fecha) return '—';
  const nac = new Date(fecha);
  if (Number.isNaN(nac.getTime())) return '—';
  const hoy = new Date();
  let anios = hoy.getFullYear() - nac.getFullYear();
  const m = hoy.getMonth() - nac.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) anios--;
  if (anios < 0) return '—';
  if (anios === 0) {
    const meses = (hoy.getFullYear() - nac.getFullYear()) * 12 + hoy.getMonth() - nac.getMonth();
    return meses <= 0 ? '< 1 mes' : `${meses} mes${meses === 1 ? '' : 'es'}`;
  }
  return `${anios} año${anios === 1 ? '' : 's'}`;
}

/** Parsea CSV simple (coma o punto y coma). Primera fila = encabezados. */
export function parseCsv(text: string): { headers: string[]; rows: Record<string, string>[] } {
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length === 0) return { headers: [], rows: [] };
  const sep = lines[0].includes(';') && !lines[0].includes(',') ? ';' : ',';
  const split = (line: string) => {
    const out: string[] = [];
    let cur = '';
    let q = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') { q = !q; continue; }
      if (!q && c === sep) { out.push(cur.trim()); cur = ''; continue; }
      cur += c;
    }
    out.push(cur.trim());
    return out;
  };
  const headers = split(lines[0]).map(h => h.toLowerCase().normalize('NFD').replace(/\p{M}/gu, ''));
  const rows = lines.slice(1).map(line => {
    const cols = split(line);
    const row: Record<string, string> = {};
    headers.forEach((h, i) => { row[h] = cols[i] ?? ''; });
    return row;
  });
  return { headers, rows };
}

export function csvCell(row: Record<string, string>, ...keys: string[]): string {
  for (const k of keys) {
    const v = row[k] ?? row[k.toLowerCase()];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return '';
}

export async function parseSpreadsheet(file: File): Promise<{ headers: string[]; rows: Record<string, string>[] }> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
    const buf = await file.arrayBuffer();
    const XLSX = await import('xlsx');
    const wb = XLSX.read(buf, { type: 'array' });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
    if (!json.length) return { headers: [], rows: [] };
    const headers = Object.keys(json[0]).map(h => h.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '').trim());
    const rows = json.map(obj => {
      const row: Record<string, string> = {};
      Object.keys(obj).forEach((k, i) => { row[headers[i] ?? k.toLowerCase()] = String(obj[k] ?? '').trim(); });
      // also map original lower keys
      Object.keys(obj).forEach(k => {
        const nk = k.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');
        row[nk] = String(obj[k] ?? '').trim();
      });
      return row;
    });
    return { headers, rows };
  }
  const text = await file.text();
  return parseCsv(text);
}
